import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || '';

const supabase = createClient(supabaseUrl, supabaseAnonKey);

const USER_UUID_KEY = 'ai_inochan_user_uuid';

/**
 * 匿名ユーザーのUUIDを管理するフック
 * - Supabase Anonymous Authが有効な場合はそちらを使用
 * - 無効な場合はcrypto.randomUUID()でUUIDを生成してlocalStorageに保存
 */
export function useAnonymousAuth() {
  const [userId, setUserId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initUser = async () => {
      try {
        // まず既存のセッションを確認
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData?.session?.user?.id) {
          setUserId(sessionData.session.user.id);
          localStorage.setItem(USER_UUID_KEY, sessionData.session.user.id);
          setIsLoading(false);
          return;
        }

        // Supabase Anonymous Authでサインイン試行
        const { data: signInData, error: signInError } = await supabase.auth.signInAnonymously();
        if (!signInError && signInData?.user?.id) {
          setUserId(signInData.user.id);
          localStorage.setItem(USER_UUID_KEY, signInData.user.id);
          setIsLoading(false);
          return;
        }
      } catch {
        // Anonymous Authが無効な場合はフォールバック
      }

      // フォールバック：localStorageに保存したUUIDを使用
      const stored = localStorage.getItem(USER_UUID_KEY);
      if (stored && isValidUUID(stored)) {
        setUserId(stored);
      } else {
        const newUuid = crypto.randomUUID();
        localStorage.setItem(USER_UUID_KEY, newUuid);
        setUserId(newUuid);
      }
      setIsLoading(false);
    };

    initUser();
  }, []);

  return { userId, isLoading };
}

function isValidUUID(str: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);
}
